import { useEffect, useState } from 'react';
import type { DocumentStatus } from '@erp-attach/shared';
import { useApi } from '../api/ApiProvider';

export type Preview =
  | { kind: 'loading' }
  | { kind: 'pdf'; url: string }
  | { kind: 'image'; url: string }
  | { kind: 'unsupported'; contentType: string }
  | { kind: 'unavailable'; reason: string }
  | { kind: 'error'; error: Error };

export interface PreviewSubject {
  id: string;
  status: DocumentStatus;
  hidden: boolean;
  canRead: boolean;
}

export function unavailableReason(status: DocumentStatus): string {
  switch (status) {
    case 'scanning':
      return 'The malware scan has not finished yet. Check back in a few minutes.';
    case 'uploading':
      return 'The upload has not completed yet.';
    case 'rejected':
      return 'This file was rejected by the scan or the document-type policy.';
    case 'purged':
      return 'This document was removed.';
    default:
      return 'You do not have permission to view this document.';
  }
}

export function isViewable(s: PreviewSubject): boolean {
  return s.canRead && (s.status === 'available' || s.status === 'hidden' || s.hidden);
}

/** True when the bytes start with the `%PDF-` signature. */
export function isPdf(blob: Blob): Promise<boolean> {
  // FileReader rather than Blob.arrayBuffer(): same result in browsers, and jsdom lacks the latter.
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String.fromCharCode(...new Uint8Array(reader.result as ArrayBuffer)) === '%PDF-');
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file'));
    reader.readAsArrayBuffer(blob.slice(0, 5));
  });
}

/**
 * Fetches the bytes with the bearer (the API audits before the first byte), hands the browser a
 * short-lived blob: URL and revokes it when the subject changes or the caller unmounts.
 */
export function useDocumentPreview(subject: PreviewSubject | null): Preview {
  const api = useApi();
  const [preview, setPreview] = useState<Preview>({ kind: 'loading' });
  const id = subject?.id ?? null;
  const status = subject?.status ?? null;
  const hidden = subject?.hidden ?? false;
  const canRead = subject?.canRead ?? false;

  useEffect(() => {
    if (!id || !status) return;
    if (!isViewable({ id, status, hidden, canRead })) {
      setPreview({ kind: 'unavailable', reason: unavailableReason(status) });
      return;
    }
    const controller = new AbortController();
    let objectUrl: string | null = null;
    setPreview({ kind: 'loading' });
    api.documents
      .content(id, controller.signal)
      .then(async ({ blob, contentType }) => {
        if (controller.signal.aborted) return;
        const type = (contentType.split(';')[0] ?? '').trim().toLowerCase();
        if (type === 'application/pdf') {
          // The PDF frame is unsandboxed (Chromium blocks its viewer otherwise), so only real PDFs reach it.
          if (!(await isPdf(blob))) {
            if (!controller.signal.aborted) setPreview({ kind: 'unsupported', contentType: type });
            return;
          }
          if (controller.signal.aborted) return;
          objectUrl = URL.createObjectURL(blob.type === type ? blob : new Blob([blob], { type }));
          setPreview({ kind: 'pdf', url: objectUrl });
        } else if (type.startsWith('image/')) {
          objectUrl = URL.createObjectURL(blob);
          setPreview({ kind: 'image', url: objectUrl });
        } else {
          setPreview({ kind: 'unsupported', contentType: type });
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setPreview({ kind: 'error', error: err instanceof Error ? err : new Error(String(err)) });
      });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [api, id, status, hidden, canRead]);

  return preview;
}
